import React, { FC, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import moment from 'moment';
import { useCalendar } from 'Components/Common/hooks/useCalendar';
import { useVisibleMinutes } from 'Components/WorkingHours/hooks/useVisibleMinutes';
import { CellSize } from 'Components/Calendar/CalendarHelper';
import { useApp } from 'Providers/AppProvider';

// Constants for maintainability
const UPDATE_INTERVAL_MS = 30000; // 30 seconds
const INDICATOR_CIRCLE_SIZE = 7; // pixels
const INDICATOR_CIRCLE_OFFSET = INDICATOR_CIRCLE_SIZE / 2; // Center the circle
const INDICATOR_Z_INDEX = 10; // Behind calendar entries
const TOOLTIP_Z_INDEX = 1000; // Above everything
const MINUTES_PER_HOUR = 60;
const TOOLTIP_OFFSET_TOP = 8; // pixels below the circle

interface Props {
	cellSize: CellSize;
}

const CurrentTimeIndicator: FC<Props> = ({ cellSize }) => {
	const [currentTime, setCurrentTime] = useState(moment());
	const selectedDate = useCalendar((state) => state.selectedDate);
	const visibleMinutes = useVisibleMinutes();
	const { isStandaloneApp } = useApp();
	const classes = useStyles();

	// Check if we should show the indicator (early check for today)
	const isToday = selectedDate.isSame(moment(), 'day');
	
	// Update current time every 30 seconds, but only if showing today
	useEffect(() => {
		// Don't run interval if not showing today's date
		if (!isToday) return;
		
		const updateTime = () => {
			setCurrentTime(moment());
		};

		updateTime();
		const interval = setInterval(updateTime, UPDATE_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [isToday]);

	// Memoize the visibility check
	const isVisible = useMemo(() => {
		if (!isToday) return false;

		const currentMinutes = currentTime.hours() * MINUTES_PER_HOUR + currentTime.minutes();
		return currentMinutes >= visibleMinutes.start && currentMinutes <= visibleMinutes.end;
	}, [isToday, currentTime, visibleMinutes.start, visibleMinutes.end]);

	// Memoize the position calculation
	const positionStyle = useMemo(() => {
		if (!isVisible) return {};

		const currentMinutes = currentTime.hours() * MINUTES_PER_HOUR + currentTime.minutes();
		const startHour = Math.floor(visibleMinutes.start / MINUTES_PER_HOUR);
		const currentHour = Math.floor(currentMinutes / MINUTES_PER_HOUR);
		const hourIndex = currentHour - startHour;
		const minutesIntoHour = currentMinutes % MINUTES_PER_HOUR;
		const hourColumnWidth = cellSize.day.hourWidth;
		const positionWithinHour = (minutesIntoHour / MINUTES_PER_HOUR) * hourColumnWidth;
		const leftPosition = cellSize.day.nameWidth + (hourIndex * hourColumnWidth) + positionWithinHour;

		const topPosition = cellSize.navigation.appHeight + cellSize.navigation.calendarHeight;

		if (isStandaloneApp) {
			return {
				position: 'fixed' as const,
				left: `${leftPosition + cellSize.offset.left}px`,
				top: `${topPosition}px`,
				height: '100vh',
			};
		}

		return {
			position: 'absolute' as const,
			left: `${leftPosition}px`,
			top: `${topPosition}px`,
			height: '100%',
		};
	}, [isVisible, currentTime, visibleMinutes.start, cellSize, isStandaloneApp]);

	// Early return if not visible
	if (!isVisible) {
		return null;
	}

	return (
		<Box
			className={classes.currentTimeIndicator}
			data-time={currentTime.format('HH:mm')}
			data-testid="current-time-indicator"
			style={positionStyle}
		/>
	);
};

const useStyles = makeStyles({
	currentTimeIndicator: {
		// Position will be set dynamically via inline styles
		width: '1px',
		backgroundColor: 'rgba(255, 87, 34, 0.6)', // More subtle orange with transparency
		zIndex: INDICATOR_Z_INDEX, // Lower z-index to stay behind calendar entries
		pointerEvents: 'none',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: `-${INDICATOR_CIRCLE_OFFSET}px`, // Center the circle vertically on the line
			left: `-${INDICATOR_CIRCLE_OFFSET}px`, // Center the circle horizontally on the line
			width: `${INDICATOR_CIRCLE_SIZE}px`,
			height: `${INDICATOR_CIRCLE_SIZE}px`,
			backgroundColor: 'rgba(255, 87, 34, 0.8)',
			borderRadius: '50%',
			pointerEvents: 'auto', // Enable hover on the circle
			cursor: 'pointer',
		},
		'&::after': {
			content: 'attr(data-time)',
			position: 'absolute',
			top: `${TOOLTIP_OFFSET_TOP}px`, // Position below the circle
			left: '50%',
			transform: 'translateX(-50%)',
			backgroundColor: 'rgba(0, 0, 0, 0.8)',
			color: 'white',
			padding: '4px 8px',
			borderRadius: '4px',
			fontSize: '12px',
			fontWeight: 500,
			whiteSpace: 'nowrap',
			opacity: 0,
			visibility: 'hidden',
			transition: 'opacity 0.2s ease, visibility 0.2s ease',
			pointerEvents: 'none',
			zIndex: TOOLTIP_Z_INDEX,
		},
		'&:hover::after': {
			opacity: 1,
			visibility: 'visible',
		},
	},
});

export default CurrentTimeIndicator; 